// Migration path: an existing clinician signed in via phone OTP and has no
// username on file. Prompt them to pick a username + password so they can
// use the password lane on subsequent sessions. Skipping is allowed (they
// can always come back via a settings screen later) — but for V0 we make
// it "required" by not offering a skip button; they've already told us they
// want password auth by using it.

import { useState } from 'react';
import { authApi, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Logo } from '../components/Logo';

export function SetCredentialsScreen() {
    const { refresh, signOut, clinician } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        if (username.trim().length < 3) {
            setError('Username must be at least 3 characters.');
            return;
        }
        if (password.length < 8) {
            setError('Password must be at least 8 characters.');
            return;
        }
        if (!/[a-zA-Z]/.test(password) || !/\d/.test(password)) {
            setError('Password must include a letter and a number.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setBusy(true);
        try {
            await authApi.setPassword({ username: username.trim(), password });
            // Re-probe /me so the auth context picks up the new username and
            // this screen unmounts.
            await refresh();
        } catch (err) {
            setError(
                err instanceof ApiError && err.code === 'username_taken'
                    ? 'That username is taken. Try another.'
                    : err instanceof ApiError && err.code === 'invalid_username'
                        ? 'Username can be 3–64 characters: letters, digits, and . _ - + @'
                        : err instanceof ApiError && err.code === 'weak_password'
                            ? 'Password too weak. Use 8+ characters, a letter, and a number.'
                            : 'Could not save. Try again.',
            );
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="min-h-full flex flex-col">
            <header className="px-6 py-4">
                <Logo size={32} />
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
            <div className="pm-card w-full max-w-md p-6">
                <div className="text-brand-700 text-xs uppercase tracking-wide font-semibold">
                    Pamosi Clinic
                </div>
                <h1 className="text-slate-900 text-xl font-semibold mt-1">
                    Set your username & password
                </h1>
                <p className="text-slate-500 text-sm mt-2">
                    Signed in as {clinician?.phone_e164 ?? 'unknown'}. Pick a username
                    and password so you can sign in without needing an SMS code next time.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="pm-label" htmlFor="set-username">Username</label>
                        <input
                            id="set-username"
                            className="pm-input"
                            type="text"
                            autoComplete="username"
                            placeholder="e.g. drfemi or you@clinic.com"
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="set-password">Password</label>
                        <input
                            id="set-password"
                            className="pm-input"
                            type="password"
                            autoComplete="new-password"
                            placeholder="At least 8 characters"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    <div>
                        <label className="pm-label" htmlFor="set-confirm">Confirm password</label>
                        <input
                            id="set-confirm"
                            className="pm-input"
                            type="password"
                            autoComplete="new-password"
                            value={confirm}
                            onChange={(e) => setConfirm(e.target.value)}
                            disabled={busy}
                        />
                    </div>
                    {error && <div className="pm-error">{error}</div>}
                    <button className="pm-btn-primary w-full" type="submit" disabled={busy}>
                        {busy ? 'Saving…' : 'Save & continue'}
                    </button>
                    <button
                        type="button"
                        className="pm-btn-ghost w-full"
                        onClick={signOut}
                        disabled={busy}
                    >
                        Sign out
                    </button>
                </form>
            </div>
            </div>
        </div>
    );
}
